import { Controller } from "@hotwired/stimulus";

// Recherche globale de la navbar (générique, par espace).
//
// Une icône dans la navbar ouvre une modale (palette de commandes) ;
// la saisie déclenche des suggestions dès MIN_CHARS caractères (debounce
// 300ms), récupérées en HTML auprès du contrôleur de l'espace courant.
// Raccourci ⌘K / Ctrl+K pour ouvrir.
//
// Locals attendus : data-navbar-search-url-value (endpoint de recherche).
export default class extends Controller {
  static targets = ["input", "panel", "modal"];
  static values = { url: String, minChars: { type: Number, default: 3 } };

  connect() {
    this._onGlobalKeydown = this._handleGlobalKeydown.bind(this);
    document.addEventListener("keydown", this._onGlobalKeydown);
    this._debounceTimer = null;
    this._activeIndex = -1;
  }

  disconnect() {
    document.removeEventListener("keydown", this._onGlobalKeydown);
    clearTimeout(this._debounceTimer);
    document.body.classList.remove("ds-gsearch-locked");
  }

  open() {
    this.modalTarget.classList.remove("hidden");
    document.body.classList.add("ds-gsearch-locked");
    requestAnimationFrame(() => {
      this.inputTarget.focus();
      this.inputTarget.select();
    });
  }

  close() {
    this.modalTarget.classList.add("hidden");
    document.body.classList.remove("ds-gsearch-locked");
    this.inputTarget.value = "";
    this.panelTarget.innerHTML = "";
    this._activeIndex = -1;
  }

  search() {
    clearTimeout(this._debounceTimer);
    const query = this.inputTarget.value.trim();

    if (query.length < this.minCharsValue) {
      this.panelTarget.innerHTML = "";
      this._activeIndex = -1;
      return;
    }

    this._debounceTimer = setTimeout(() => this._fetch(query), 300);
  }

  onKeydown(event) {
    switch (event.key) {
      case "Escape":
        this.close();
        break;
      case "ArrowDown":
        event.preventDefault();
        this._move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this._move(-1);
        break;
      case "Enter": {
        const active = this._items()[this._activeIndex];
        if (active) {
          event.preventDefault();
          active.click();
        }
        break;
      }
      default:
        break;
    }
  }

  // ── Privé ──

  async _fetch(query) {
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`;
    // Annule la requête précédente et ignore toute réponse obsolète : avec
    // des réponses hors-ordre, une vieille requête ne doit pas écraser
    // les résultats courants (ni repeupler un champ vidé).
    this._abort?.abort();
    const controller = new AbortController();
    this._abort = controller;
    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        signal: controller.signal,
      });
      if (controller.signal.aborted || query !== this.inputTarget.value.trim()) {
        return;
      }
      if (!response.ok) {
        this.panelTarget.innerHTML = "";
        return;
      }
      this.panelTarget.innerHTML = await response.text();
      window.g4sRenderIcons?.();
      this._activeIndex = -1;
    } catch (error) {
      if (error.name !== "AbortError") this.panelTarget.innerHTML = "";
    }
  }

  _items() {
    return Array.from(this.panelTarget.querySelectorAll(".ds-gsearch__item"));
  }

  _move(delta) {
    const items = this._items();
    if (!items.length) return;
    this._activeIndex =
      (this._activeIndex + delta + items.length) % items.length;
    items.forEach((el, i) =>
      el.classList.toggle("is-active", i === this._activeIndex),
    );
    items[this._activeIndex].scrollIntoView({ block: "nearest" });
  }

  _handleGlobalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      this.open();
    }
  }
}
